import { memo, useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Alert, Button, Col, Container, Form, ProgressBar, Row, Spinner, Table } from "react-bootstrap";
import { ChevronDown, ChevronUp } from "react-bootstrap-icons";
import QuickForm from "../../Components/Forms/QuickForm/QuickForm";
import useMyProperties from "../../Hooks/UseMyProperties/UseMyPropertiesHook";
import timeService from "../../services/time/timeService";
import { useAppDispatch } from "../../redux/store";
import useBookingsByPropertyMemo from "../../Memos/Bookings/UseBookingsByPropertyMemo";
import PropertyMultiSelect from "../../Components/PropertyMultiSelect/PropertyMultiSelectNew";
import './adrOccupancyReporter.scss'
import { Link } from "react-router-dom";
import {
    DATE_FILTER_FORM,
    TABLE_MODE_BUTTONS,
    dateHasher,
    generateCSVURL,
    generatePropertyReportData,
    generatePropertyTotalsReport,
    getSelectedPropertiesFromLocal,
    numberFormatter,
    saveSelectedPropertiesToLocal
} from "./adrOccupancyHelpersNew";
import useActiveQueryHook from "../../Hooks/UseActiveQuery/useActiveQueryHook";
import { convertActiveQueryToUrl } from "../../Helpers/Query/queryHelpers";
import { navigateTo } from "../../redux/router/routerActions";
import { useGetBookingsQuery } from "../../services/bClientApi";
import { dayOfPayouts } from "../../Helpers/Bookings/bookingHelpers";
import { getRole } from "../../services/accessService/accessService";
import Collapsible from "../../Components/Collapsible/Collapsible";
import PayoutDisclaimer from "../../Components/Disclaimers/PayoutDisclaimer";

const calculateMedian = (values: number[]) => {
    const sortedValues = [...values].sort((a, b) => a - b)
    const middleIndex = Math.floor(sortedValues.length / 2)

    return sortedValues.length % 2 === 0
        ? (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2
        : sortedValues[middleIndex]
}

type PropertyReportRowProps = {
    propertyReport: any
    dateRangeArray: string[]
    dateRangeHash: Record<string, any>
    myProperties: any
    tableMode: string
    percentageMode: boolean
    isSelected: boolean
    onToggleRemoval: (propertyId: string) => void
}


type ReportTableSelectionActions = {
    togglePropertyRemoval: (propertyId: string) => void
    removeSelectedProperties: () => void
    removeInactiveProperties: () => void
    clearPropertyRemoval: () => void
    hasSelectedProperties: boolean
    propertiesToRemove: Set<string>
}

type ReportTableSelectionBoundaryProps = {
    onRemoveProperties: (propertyIds: Set<string>) => void
    onRemoveInactiveProperties: () => void
    children: (actions: ReportTableSelectionActions) => React.ReactNode
}

function ReportTableSelectionBoundary({
    onRemoveProperties,
    onRemoveInactiveProperties,
    children,
}: ReportTableSelectionBoundaryProps) {
    const [propertiesToRemove, setPropertiesToRemove] = useState<Set<string>>(() => new Set())

    const togglePropertyRemoval = useCallback((propertyId: string) => {
        setPropertiesToRemove((currentProperties) => {
            const nextProperties = new Set(currentProperties)
            if (nextProperties.has(propertyId)) {
                nextProperties.delete(propertyId)
            } else {
                nextProperties.add(propertyId)
            }
            return nextProperties
        })
    }, [])

    const clearPropertyRemoval = () => {
        setPropertiesToRemove(new Set())
    }

    const removeSelectedProperties = () => {
        if (propertiesToRemove.size === 0) {
            return
        }
        onRemoveProperties(propertiesToRemove)
        clearPropertyRemoval()
    }

    const removeInactiveProperties = () => {
        onRemoveInactiveProperties()
        clearPropertyRemoval()
    }

    return (
        <>
            {children({
                togglePropertyRemoval,
                removeSelectedProperties,
                removeInactiveProperties,
                clearPropertyRemoval,
                hasSelectedProperties: propertiesToRemove.size > 0,
                propertiesToRemove,
            })}
        </>
    )
}

const PropertyReportRow = memo(function PropertyReportRow({
    propertyReport,
    dateRangeArray,
    dateRangeHash,
    myProperties,
    tableMode,
    percentageMode,
    isSelected,
    onToggleRemoval,
}: PropertyReportRowProps) {
    return (
        <tr className={isSelected ? 'property-report-row-selected' : undefined}>
            <td
                className="property-report-selection-cell"
                onClick={(event) => {
                    const target = event.target as HTMLElement
                    if (!target.closest('input, label')) {
                        onToggleRemoval(propertyReport.propertyId)
                    }
                }}
            >
                <Form.Check
                    type="checkbox"
                    id={`remove-property-${propertyReport.propertyId}`}
                    label={propertyReport.shortTitle}
                    checked={isSelected}
                    onChange={() => onToggleRemoval(propertyReport.propertyId)}
                />
            </td>
            {dateRangeArray.map((d: string) => {
                const checkInTimeStamp = timeService.createLuxonDate(new Date(dateRangeHash[d].timeStamp)).startOf('month').toMillis()
                const monthStart = timeService.createLuxonDate(`${d}-01`)
                const monthEnd = monthStart.endOf('month').endOf('day').toMillis()
                const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0

                // If the property was created after this month, show N/A
                if (created > monthEnd) {
                    return (
                        <td key={d}>
                            <div className="number-wrapper me-3" title="Property did not exist">
                                N/A
                            </div>
                        </td>
                    )
                }

                const formattedNumber = numberFormatter(propertyReport.mappedData[d][tableMode], tableMode)
                const percentageValue = (propertyReport.mappedData[d][tableMode] / propertyReport.totals[tableMode]) * 100
                const percentageNumber = `${+((percentageValue ? percentageValue : 0).toFixed(2))}%`
                return (
                    <td key={d}>
                        <div className="number-wrapper me-3" title={percentageMode ? formattedNumber : percentageNumber}>
                            {percentageMode ? percentageNumber : formattedNumber}
                        </div>
                        <Link className="corner-link text-decoration-none" to={`/financials-property/${propertyReport.propertyId}?checkIn=${checkInTimeStamp}`}>
                            ◥
                        </Link>
                        {tableMode === 'trueOccupancy' ? (
                            <ProgressBar now={propertyReport.mappedData[d].trueOccupancy} />
                        ) : (
                            <ProgressBar now={propertyReport.mappedData[d].occupancy} />
                        )}
                    </td>
                )
            })}
            <td>
                <div className="number-wrapper me-3">
                    {numberFormatter(propertyReport.totals[tableMode], tableMode)}
                </div>
                {tableMode === 'trueOccupancy' ? (
                    <ProgressBar now={propertyReport.totals.trueOccupancy} />
                ) : (
                    <ProgressBar now={propertyReport.totals.occupancy} />
                )}
            </td>
        </tr>
    )
})

export default function AdrOccupancyReporterPage() {
    const currentRole = getRole()

    // earnings, adr, occupancy, leadTime
    const [tableMode, setTableMode] = useState<string>(currentRole === 'owner' ? 'payout' : 'earnings')

    const dispatch = useAppDispatch()
    const { myProperties, isLoading: myPropertiesLoading } = useMyProperties();

    const [selectedProperties, setSelectedProperties] = useState<any>(getSelectedPropertiesFromLocal());
    const [percentageMode, setPercentageMode] = useState<boolean>(false)

    const [advancedMode, setAdvancedMode] = useState<boolean>(false)

    const [showAverageRow, setShowAverageRow] = useState(false)
    const [showMedianRow, setShowMedianRow] = useState(false)

    const [fileUrl, setFileUrl] = useState<string>('')

    const [queryPropertyIds, setQueryPropertyIds] = useState<string[]>(Object.keys(selectedProperties))
    const [pendingPropertyFetch, setPendingPropertyFetch] = useState(false)

    const setSelectedPropertiesMemo = useCallback((sp: any) => {
        saveSelectedPropertiesToLocal(sp)
        setSelectedProperties(sp)
        setQueryPropertyIds((currentQueryPropertyIds) => {
            const newIds = Object.keys(sp)
            const haveSameIds = newIds.length === currentQueryPropertyIds.length && newIds.every((id) => currentQueryPropertyIds.includes(id))
            if (haveSameIds) {
                return currentQueryPropertyIds
            }

            const hasNewProperty = newIds.some((propertyId) => !currentQueryPropertyIds.includes(propertyId))
            if (hasNewProperty) {
                setPendingPropertyFetch(true)
            }
            return newIds
        })
    }, [])

    const defaultQuery = useMemo(() => ({
        query: {
            checkIn: timeService.getCurrentTime().startOf('month').startOf('day').toMillis(),
            checkOut: timeService.getCurrentTime().endOf('month').endOf('day').toMillis(),
            bookingStatus: ['confirmed', 'new', 'pending'],
        },
        page: 1,
        limit: 300,
        all: true
    }), [])

    const { activeQuery } = useActiveQueryHook(defaultQuery)

    const propertyIds = useMemo(() => Object.keys(selectedProperties), [selectedProperties])

    const shouldQueryData = !activeQuery.query.checkIn || !activeQuery.query.checkOut || propertyIds.length === 0

    const bookingsQueryArgs = useMemo(() => {
        const { propertyId, ...queryWithoutPropertyId } = activeQuery.query
        delete queryWithoutPropertyId.excludeSource
        return {
            bookingsQuery: {
                ...queryWithoutPropertyId,
                propertyId: queryPropertyIds,
            },
            pagination: {
                page: activeQuery.page,
                limit: activeQuery.limit,
                all: true,
                sort: {}
            },
        }
    }, [activeQuery, queryPropertyIds])

    const directBookingsQueryArgs = useMemo(() => {
        const { propertyId, ...queryWithoutPropertyId } = activeQuery.query
        return {
            bookingsQuery: {
                source: Object.keys(dayOfPayouts),
                bookingStatus: ['confirmed', 'new', 'pending'],
                dateBookedStart: queryWithoutPropertyId.checkIn,
                dateBookedEnd: queryWithoutPropertyId.checkOut,
                propertyId: queryPropertyIds,
            },
            pagination: {
                page: activeQuery.page,
                limit: activeQuery.limit,
                all: true,
                sort: {}
            },
        }
    }, [activeQuery, queryPropertyIds])

    const {
        data,
        isLoading: bookingListLoading,
        isFetching: bookingListFetching,
    } = useGetBookingsQuery(bookingsQueryArgs, {
        skip: shouldQueryData,
        refetchOnFocus: false,
        refetchOnReconnect: false,
    })

    const { data: directBookingsData, isFetching: directBookingsFetching } = useGetBookingsQuery(directBookingsQueryArgs, {
        skip: shouldQueryData,
        refetchOnFocus: false,
        refetchOnReconnect: false,
    })

    useEffect(() => {
        if (!pendingPropertyFetch) {
            return
        }

        if (!bookingListFetching && !directBookingsFetching) {
            setPendingPropertyFetch(false)
        }
    }, [pendingPropertyFetch, bookingListFetching, directBookingsFetching])

    const dataDocs = useMemo(() => data?.docs || [], [data?.docs])
    const directBookingsDocs = useMemo(() => directBookingsData?.docs || [], [directBookingsData?.docs])

    const comboBookings = useMemo(() => {
        const existingIds: {
            [id: string]: boolean
        } = {}
        const bookings: any[] = [...dataDocs, ...directBookingsDocs]

        return bookings.filter((b) => {
            if (existingIds[b._id]) {
                return false
            }
            existingIds[b._id] = true
            return true
        })
    }, [dataDocs, directBookingsDocs])

    const bookingsByProperty = useBookingsByPropertyMemo(comboBookings, { propertyData: myProperties });
    const startDate = activeQuery.query.checkIn
    const endDate = activeQuery.query.checkOut

    const asOfDate = activeQuery.query.asOf

    const filteredBookingsByProperty = useMemo(() => {
        if (!bookingsByProperty || bookingsByProperty.length === 0) {
            return []
        }

        return bookingsByProperty
            .filter((e: any) => selectedProperties && selectedProperties[e.propertyId])
            .map((e: any) => ({
                propertyId: e.propertyId,
                bookings: e.bookings.filter((b: any) => {
                    if (asOfDate && b.dateBooked) {
                        return new Date(b.dateBooked).getTime() <= asOfDate
                    }
                    return true
                })
            }))
    }, [bookingsByProperty, selectedProperties, asOfDate])

    const emptyReportData = useMemo(() => ({
        propertyReportData: [] as any[],
        propertyTotalsReport: {
            totalsReport: {},
            totalsByMonth: {}
        },
        dateRangeArray: [] as string[],
        dateRangeHash: {} as Record<string, any>
    }), [])

    const lastComputedReport = useRef(emptyReportData)

    const fetching = (bookingListFetching || directBookingsFetching || pendingPropertyFetch)

    const isDataReady =
        !shouldQueryData &&
        !bookingListFetching &&
        !directBookingsFetching &&
        data !== undefined &&
        directBookingsData !== undefined &&
        !pendingPropertyFetch

    const hasWork =
        propertyIds.length > 0 &&
        Object.keys(myProperties).length > 0;

    const shouldComputePropertyReport = isDataReady && hasWork

    const {
        propertyReportData,
        propertyTotalsReport,
        dateRangeArray,
        dateRangeHash
    } = useMemo(() => {
        if (!shouldComputePropertyReport) {
            return lastComputedReport.current
        }

        const dateRangeHash = dateHasher(
            startDate,
            endDate,
            { dateFormat: 'yyyy-MM' }
        )
        const dateRangeArray = Object.keys(dateRangeHash)

        const propertyReportData = generatePropertyReportData(
            myProperties,
            selectedProperties,
            filteredBookingsByProperty,
            startDate,
            endDate,
        )

        const propertyTotalsReport = generatePropertyTotalsReport(
            propertyReportData,
            myProperties,
            startDate,
            endDate,
        )

        const computedReport = {
            propertyReportData,
            propertyTotalsReport,
            dateRangeArray,
            dateRangeHash
        }

        lastComputedReport.current = computedReport
        return computedReport
    }, [shouldComputePropertyReport, startDate, endDate, myProperties, selectedProperties, filteredBookingsByProperty])

    const monthCount = useMemo(() => {
        if (!startDate || !endDate) {
            return 0
        }

        return Object.keys(dateHasher(startDate, endDate, { dateFormat: 'yyyy-MM' })).length
    }, [startDate, endDate])

    const inactivePropertyIds = useMemo(() => propertyReportData
        .filter((propertyReport: any) => dateRangeArray.some((d: string) => {
            const monthEnd = timeService.createLuxonDate(`${d}-01`).endOf('month').endOf('day').toMillis()
            const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
            return created > monthEnd
        }))
        .map((propertyReport: any) => propertyReport.propertyId), [propertyReportData, dateRangeArray, myProperties])

    const inactivePropertyCount = inactivePropertyIds.length

    const submitDateFilterForm = (data: any) => {
        const newQuery: any = {
            ...activeQuery,
            query: {
                checkIn: timeService.createLuxonDate(new Date(data.checkIn)).startOf('day').toMillis(),
                checkOut: timeService.createLuxonDate(new Date(data.checkOut)).endOf('day').toMillis(),
                bookingStatus: ['confirmed', 'new', 'pending'],
            }
        }
        if (data.asOf) {
            newQuery.query.asOf = timeService.createLuxonDate(new Date(data.asOf)).endOf('day').toMillis()
        }
        if (propertyIds && propertyIds.length > 0) {
            newQuery.query['propertyId'] = propertyIds;
        }

        const pageParams = JSON.parse(JSON.stringify(newQuery))
        delete pageParams.query.propertyId
        const destination = `/adr-occupancy-reporter?${convertActiveQueryToUrl(pageParams)}`
        dispatch(navigateTo(destination))
    }

    const shiftAsOfDate = (duration: { months?: number; years?: number }) => {
        if (!asOfDate) {
            return undefined
        }

        return timeService.createLuxonDate(new Date(asOfDate)).plus(duration).endOf('day').toMillis()
    }

    const generateCSV = () => {
        const csvUrl = generateCSVURL(myProperties, propertyReportData, propertyTotalsReport)
        setFileUrl(csvUrl)
    }

    const removeSelectedProperties = (propertiesToRemove: Set<string>) => {
        if (propertiesToRemove.size === 0) {
            return
        }

        const updatedSelectedProperties = Object.keys(selectedProperties).reduce((properties, propertyId) => {
            if (!propertiesToRemove.has(propertyId)) {
                properties[propertyId] = selectedProperties[propertyId]
            }
            return properties
        }, {} as any)

        setSelectedPropertiesMemo(updatedSelectedProperties)
    }

    const removeInactiveProperties = () => {
        const inactivePropertyIdSet = new Set(inactivePropertyIds)
        const updatedSelectedProperties = Object.keys(selectedProperties).reduce((properties, propertyId) => {
            if (!inactivePropertyIdSet.has(propertyId)) {
                properties[propertyId] = selectedProperties[propertyId]
            }
            return properties
        }, {} as any)

        setSelectedPropertiesMemo(updatedSelectedProperties)
    }

    return (
        <Container>
            <Row>
                <Col>
                    <h1>ADR Occupancy Reporter Page</h1>
                    <hr />

                    <div className="text-center">
                        {/* back one year button */}
                        <Button
                            className="me-2 mb-2"
                            variant="outline-secondary"
                            size="sm"
                            disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                            onClick={() => {
                                const currentSelectedStartDate = activeQuery.query.checkIn
                                const currentSelectedEndDate = activeQuery.query.checkOut
                                const startDate = timeService.createLuxonDate(new Date(currentSelectedStartDate)).minus({ years: 1 }).startOf('month').startOf('day').toMillis()
                                const endDate = timeService.createLuxonDate(new Date(currentSelectedEndDate)).minus({ years: 1 }).endOf('month').endOf('day').toMillis()
                                submitDateFilterForm({
                                    checkIn: startDate,
                                    checkOut: endDate,
                                    asOf: shiftAsOfDate({ years: -1 }),
                                })
                            }}>
                            &larr; 1 Yr
                        </Button>

                        {/* back 1 month button */}
                        <Button
                            className="me-2 mb-2"
                            variant="outline-secondary"
                            size="sm"
                            disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                            onClick={() => {
                                const currentSelectedStartDate = activeQuery.query.checkIn
                                const currentSelectedEndDate = activeQuery.query.checkOut
                                const startDate = timeService.createLuxonDate(new Date(currentSelectedStartDate)).minus({ months: 1 }).startOf('month').startOf('day').toMillis()
                                const endDate = timeService.createLuxonDate(new Date(currentSelectedEndDate)).minus({ months: 1 }).endOf('month').endOf('day').toMillis()
                                submitDateFilterForm({
                                    checkIn: startDate,
                                    checkOut: endDate,
                                    asOf: shiftAsOfDate({ months: -1 }),
                                })
                            }}>
                            &larr; 1 Mo
                        </Button>


                        {/* this month button */}
                        <Button
                            className="me-2 mb-2"
                            variant="secondary"
                            size="sm"
                            disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                            onClick={() => {
                                const startDate = timeService.getCurrentTime().startOf('month').startOf('day').toMillis()
                                const endDate = timeService.getCurrentTime().endOf('month').endOf('day').toMillis()
                                submitDateFilterForm({
                                    checkIn: startDate,
                                    checkOut: endDate,
                                })
                            }}>
                            This Month
                        </Button>

                        {/* this year button */}
                        <Button                            className="me-2 mb-2"
                            variant="secondary"
                            size="sm"
                            disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                            onClick={() => {
                                const startDate = timeService.getCurrentTime().startOf('year').startOf('day').toMillis()
                                const endDate = timeService.getCurrentTime().endOf('year').endOf('day').toMillis()
                                submitDateFilterForm({
                                    checkIn: startDate,
                                    checkOut: endDate,
                                })
                            }}>
                            This Year
                        </Button>

                        {/* Year to date button */}
                        <Button
                            className="me-2 mb-2"
                            variant="secondary"
                            size="sm"
                            disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                            onClick={() => {
                                const startDate = timeService.getCurrentTime().startOf('year').startOf('day').toMillis()
                                const endDate = timeService.getCurrentTime().endOf('month').endOf('day').toMillis()
                                submitDateFilterForm({
                                    checkIn: startDate,
                                    checkOut: endDate,
                                })
                            }}>
                            YTD
                        </Button>

                        {/* last 12 months button */}
                        <Button
                            className="me-2 mb-2"
                            variant="secondary"
                            size="sm"
                            disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                            onClick={() => {
                                const currentDate = timeService.getCurrentTime()
                                const startDate = currentDate.minus({ months: 11 }).startOf('month').startOf('day').toMillis()
                                const endDate = currentDate.endOf('month').endOf('day').toMillis()
                                submitDateFilterForm({
                                    checkIn: startDate,
                                    checkOut: endDate,
                                })
                            }}>
                            Last 12 Months
                        </Button>

                        {/* forward 1 month */}
                        <Button
                            className="me-2 mb-2"
                            variant="outline-secondary"
                            size="sm"
                            disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                            onClick={() => {
                                const currentSelectedStartDate = activeQuery.query.checkIn
                                const currentSelectedEndDate = activeQuery.query.checkOut
                                const startDate = timeService.createLuxonDate(new Date(currentSelectedStartDate)).plus({ months: 1 }).startOf('month').startOf('day').toMillis()
                                const endDate = timeService.createLuxonDate(new Date(currentSelectedEndDate)).plus({ months: 1 }).endOf('month').endOf('day').toMillis()
                                submitDateFilterForm({
                                    checkIn: startDate,
                                    checkOut: endDate,
                                    asOf: shiftAsOfDate({ months: 1 }),
                                })
                            }}>
                            1 Mo &rarr;
                        </Button>

                        {/* forward 1 year */}
                        <Button
                            className="me-2 mb-2"
                            variant="outline-secondary"
                            size="sm"
                            disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                            onClick={() => {
                                const currentSelectedStartDate = activeQuery.query.checkIn
                                const currentSelectedEndDate = activeQuery.query.checkOut
                                const startDate = timeService.createLuxonDate(new Date(currentSelectedStartDate)).plus({ years: 1 }).startOf('month').startOf('day').toMillis()
                                const endDate = timeService.createLuxonDate(new Date(currentSelectedEndDate)).plus({ years: 1 }).endOf('month').endOf('day').toMillis()
                                submitDateFilterForm({
                                    checkIn: startDate,
                                    checkOut: endDate,
                                    asOf: shiftAsOfDate({ years: 1 }),
                                })
                            }}>
                            1 Yr &rarr;
                        </Button>
                    </div>

                    <hr />
                    <h4>Period: {monthCount} {monthCount > 1 ? "months" : "month"}</h4>
                    <QuickForm
                        key={`${startDate}-${endDate}-${asOfDate}`}
                        initialData={{
                            checkIn: startDate,
                            checkOut: endDate,
                            asOf: asOfDate,
                        }}
                        fields={DATE_FILTER_FORM}
                        onSubmit={submitDateFilterForm}
                        disabled={bookingListLoading || bookingListFetching || directBookingsFetching}
                        submitText={'Generate Report'}
                        onCancel={Object.keys(selectedProperties).length > 0 ? () => {
                            setSelectedPropertiesMemo({})
                        } : undefined}
                        cancelText={Object.keys(selectedProperties).length > 0 ? "Clear Properties" : undefined}
                    />

                    {Object.keys(selectedProperties).length > 0 && !(bookingListLoading) && JSON.stringify({
                        checkIn: activeQuery.query.checkIn,
                        checkOut: activeQuery.query.checkOut,
                    }) === JSON.stringify({
                        checkIn: startDate,
                        checkOut: endDate,
                    }) && comboBookings && (
                            <ReportTableSelectionBoundary
                                onRemoveProperties={removeSelectedProperties}
                                onRemoveInactiveProperties={removeInactiveProperties}
                            >
                                {({
                                    togglePropertyRemoval,
                                    removeSelectedProperties: removeSelected,
                                    removeInactiveProperties: removeInactive,
                                    clearPropertyRemoval,
                                    hasSelectedProperties,
                                    propertiesToRemove,
                                }) => (
                                    <div className="report-table-container">
                                        {propertyReportData.some((p: any) => p.overlaps && p.overlaps.length > 0) && (
                                            <Alert variant="warning">
                                                <Collapsible
                                                    title="Overlapping Bookings Detected"
                                                    titleClass="mb-0"
                                                    containerClass="mb-0"
                                                >
                                                    <ul className="mb-0 mt-1">
                                                        {propertyReportData
                                                            .filter((p: any) => p.overlaps && p.overlaps.length > 0)
                                                            .map((p: any) =>
                                                                p.overlaps.map((o: any, idx: number) => (
                                                                    <li key={`${p.propertyId}-overlap-${idx}`}>
                                                                        <strong>{p.shortTitle}</strong> on {o.date} — {o.bookings.length} bookings overlap
                                                                    </li>
                                                                ))
                                                            )}
                                                    </ul>
                                                </Collapsible>
                                            </Alert>
                                        )}
                                        <div className="table-button-container">
                                            <Button
                                                className="me-2 mb-2"
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setPercentageMode(!percentageMode)}
                                                active={percentageMode}>
                                                % Mode
                                            </Button>

                                            <Button
                                                className="me-4 mb-2"
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setAdvancedMode(!advancedMode)}
                                                active={advancedMode}>
                                                * Mode
                                            </Button>

                                            {TABLE_MODE_BUTTONS.map((b: any, idx) => {
                                                if (currentRole === 'owner' && b.value === 'earnings') {
                                                    return null
                                                }
                                                if (b.advanced && !advancedMode) {
                                                    return null
                                                }
                                                return (
                                                    <Button
                                                        className="me-2 mb-2"
                                                        variant={tableMode === b.value ? 'primary' : 'outline-primary'}
                                                        size="sm"
                                                        disabled={tableMode === b.value}
                                                        onClick={() => setTableMode(b.value)}
                                                        active={tableMode === b.value}
                                                        key={idx}>
                                                        {b.label}
                                                    </Button>
                                                )
                                            })}

                                            <Button
                                                className="me-2 mb-2"
                                                variant="primary"
                                                size="sm"
                                                onClick={() => {
                                                    generateCSV()
                                                }}>
                                                Download Report
                                            </Button>

                                            <Button
                                                className="me-2 mb-2"
                                                variant="danger"
                                                size="sm"
                                                disabled={!hasSelectedProperties}
                                                onClick={removeSelected}>
                                                Remove Selected
                                            </Button>

                                            <Button
                                                className="me-2 mb-2"
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={!hasSelectedProperties}
                                                onClick={clearPropertyRemoval}>
                                                Clear Selection
                                            </Button>

                                            
                                            <Button
                                                className="me-2 mb-2"
                                                variant="secondary"
                                                size="sm"
                                                disabled={inactivePropertyCount < 1}
                                                onClick={removeInactive}>
                                                Remove Inactive
                                            </Button>

                                            {fileUrl && (
                                                <a className="btn btn-sm btn-success me-2 mb-2" href={fileUrl} download="exported-data.csv">Download File</a>
                                            )}

                                            {tableMode === 'payout' && (
                                                <PayoutDisclaimer />
                                            )}
                                        </div>
                                        {propertyTotalsReport?.totalsByMonth && Object.keys(propertyTotalsReport.totalsByMonth).length > 0 && (
                                            <div className="table-wrapper">
                                                <Table size="sm" bordered hover>
                                                    <thead>
                                                        <tr>
                                                            <th>Property</th>
                                                            {dateRangeArray.map((d: string) => (
                                                                <th key={d}>{d}</th>
                                                            ))}
                                                            <th>Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>
                                                                <div className="d-flex">
                                                                    Totals ({Object.keys(selectedProperties).length}) <span className="ms-1 text-danger">({inactivePropertyCount})</span>{' '}
                                                                    <Button
                                                                        variant="link"
                                                                        size="sm"
                                                                        className="p-0 text-decoration-none text-dark ms-1"
                                                                        onClick={() => {
                                                                            setShowAverageRow(!showAverageRow)
                                                                            setShowMedianRow(!showMedianRow)
                                                                        }}
                                                                        aria-label={showAverageRow || showMedianRow ? 'Hide average and median rows' : 'Show average and median rows'}
                                                                        title={showAverageRow || showMedianRow ? 'Hide average and median rows' : 'Show average and median rows'}
                                                                    >
                                                                        {showAverageRow || showMedianRow ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                            {dateRangeArray.map((d: string) => {
                                                                const formattedNumber = numberFormatter(propertyTotalsReport.totalsByMonth[d][tableMode], tableMode)
                                                                const percentageValue = (propertyTotalsReport.totalsByMonth[d][tableMode] / propertyTotalsReport.totalsReport[tableMode]) * 100
                                                                const percentageNumber = `${+((percentageValue ? percentageValue : 0).toFixed(2))}%`
                                                                return (
                                                                    <td key={`${d}-${tableMode}`}>
                                                                        <div className="number-wrapper me-3" title={percentageMode ? formattedNumber : percentageNumber}>
                                                                            {percentageMode ? percentageNumber : formattedNumber}
                                                                        </div>
                                                                        {tableMode === 'trueOccupancy' ? (
                                                                            <ProgressBar now={propertyTotalsReport.totalsByMonth[d].trueOccupancy} />
                                                                        ) : (
                                                                            <ProgressBar now={propertyTotalsReport.totalsByMonth[d].occupancy} />
                                                                        )}
                                                                    </td>
                                                                )
                                                            })}
                                                            <td>
                                                                <div className="number-wrapper me-3">
                                                                    {numberFormatter(propertyTotalsReport.totalsReport[tableMode], tableMode)}
                                                                </div>
                                                                {tableMode === 'trueOccupancy' ? (
                                                                    <ProgressBar now={propertyTotalsReport.totalsReport.trueOccupancy} />
                                                                ) : (
                                                                    <ProgressBar now={propertyTotalsReport.totalsReport.occupancy} />
                                                                )}
                                                            </td>
                                                        </tr>
                                                        {showAverageRow && (
                                                        <tr>
                                                            <td>Average ({Object.keys(selectedProperties).length}) <span className="text-danger">({inactivePropertyCount})</span></td>
                                                            {dateRangeArray.map((d: string) => {
                                                                const monthStart = timeService.createLuxonDate(`${d}-01`)
                                                                const monthEnd = monthStart.endOf('month').endOf('day').toMillis()
                                                                const existingPropsForMonth = propertyReportData.filter((propertyReport) => {
                                                                    const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
                                                                    return created <= monthEnd
                                                                })

                                                                const count = existingPropsForMonth.length || 0
                                                                const averageValue = count ? existingPropsForMonth.reduce((sum, propertyReport) => sum + (propertyReport.mappedData[d] ? propertyReport.mappedData[d][tableMode] : 0), 0) / count : 0
                                                                const averageTotal = count ? existingPropsForMonth.reduce((sum, propertyReport) => sum + (propertyReport.totals ? propertyReport.totals[tableMode] : 0), 0) / count : 0
                                                                const formattedNumber = numberFormatter(averageValue, tableMode)
                                                                const percentageValue = (averageTotal ? (averageValue / averageTotal) * 100 : 0)
                                                                const percentageNumber = `${+((percentageValue ? percentageValue : 0).toFixed(2))}%`
                                                                const averageOccupancy = count ? existingPropsForMonth.reduce((sum, propertyReport) => sum + (propertyReport.mappedData[d] ? propertyReport.mappedData[d].occupancy : 0), 0) / count : 0
                                                                const averageTrueOccupancy = count ? existingPropsForMonth.reduce((sum, propertyReport) => sum + (propertyReport.mappedData[d] ? propertyReport.mappedData[d].trueOccupancy : 0), 0) / count : 0
                                                                return (
                                                                    <td key={`average-${d}-${tableMode}`}>
                                                                        <div className="number-wrapper me-3" title={percentageMode ? formattedNumber : percentageNumber}>
                                                                            {percentageMode ? percentageNumber : formattedNumber}
                                                                        </div>
                                                                        {tableMode === 'trueOccupancy' ? (
                                                                            <ProgressBar now={averageTrueOccupancy} />
                                                                        ) : (
                                                                            <ProgressBar now={averageOccupancy} />
                                                                        )}
                                                                    </td>
                                                                )
                                                            })}
                                                            <td>
                                                                <div className="number-wrapper me-3">
                                                                    {numberFormatter(
                                                                        (() => {
                                                                            const included = propertyReportData.filter((propertyReport) => {
                                                                                const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
                                                                                return created <= endDate
                                                                            })
                                                                            const count = included.length || 0
                                                                            return count ? included.reduce((sum, propertyReport) => sum + (propertyReport.totals ? propertyReport.totals[tableMode] : 0), 0) / count : 0
                                                                        })(),
                                                                        tableMode,
                                                                    )}
                                                                </div>
                                                                {tableMode === 'trueOccupancy' ? (
                                                                    <ProgressBar now={(() => {
                                                                        const included = propertyReportData.filter((propertyReport) => {
                                                                            const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
                                                                            return created <= endDate
                                                                        })
                                                                        const count = included.length || 0
                                                                        return count ? included.reduce((sum, propertyReport) => sum + propertyReport.totals.trueOccupancy, 0) / count : 0
                                                                    })()} />
                                                                ) : (
                                                                    <ProgressBar now={(() => {
                                                                        const included = propertyReportData.filter((propertyReport) => {
                                                                            const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
                                                                            return created <= endDate
                                                                        })
                                                                        const count = included.length || 0
                                                                        return count ? included.reduce((sum, propertyReport) => sum + propertyReport.totals.occupancy, 0) / count : 0
                                                                    })()} />
                                                                )}
                                                            </td>
                                                        </tr>
                                                        )}
                                                        {showMedianRow && (
                                                        <tr>
                                                            <td>Median ({Object.keys(selectedProperties).length}) <span className="text-danger">({inactivePropertyCount})</span></td>
                                                            {dateRangeArray.map((d: string) => {
                                                                const monthStart = timeService.createLuxonDate(`${d}-01`)
                                                                const monthEnd = monthStart.endOf('month').endOf('day').toMillis()
                                                                const existingPropsForMonth = propertyReportData.filter((propertyReport) => {
                                                                    const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
                                                                    return created <= monthEnd
                                                                })

                                                                const values = existingPropsForMonth.map((propertyReport) => (propertyReport.mappedData[d] ? propertyReport.mappedData[d][tableMode] : 0))
                                                                const totalsValues = existingPropsForMonth.map((propertyReport) => (propertyReport.totals ? propertyReport.totals[tableMode] : 0))
                                                                const medianValue = values.length ? calculateMedian(values) : 0
                                                                const medianTotal = totalsValues.length ? calculateMedian(totalsValues) : 0
                                                                const formattedNumber = numberFormatter(medianValue, tableMode)
                                                                const percentageValue = (medianTotal ? (medianValue / medianTotal) * 100 : 0)
                                                                const percentageNumber = `${+((percentageValue ? percentageValue : 0).toFixed(2))}%`
                                                                const medianOccupancy = values.length ? calculateMedian(existingPropsForMonth.map((propertyReport) => (propertyReport.mappedData[d] ? propertyReport.mappedData[d].occupancy : 0))) : 0
                                                                const medianTrueOccupancy = values.length ? calculateMedian(existingPropsForMonth.map((propertyReport) => (propertyReport.mappedData[d] ? propertyReport.mappedData[d].trueOccupancy : 0))) : 0
                                                                return (
                                                                    <td key={`median-${d}-${tableMode}`}>
                                                                        <div className="number-wrapper me-3" title={percentageMode ? formattedNumber : percentageNumber}>
                                                                            {percentageMode ? percentageNumber : formattedNumber}
                                                                        </div>
                                                                        {tableMode === 'trueOccupancy' ? (
                                                                            <ProgressBar now={medianTrueOccupancy} />
                                                                        ) : (
                                                                            <ProgressBar now={medianOccupancy} />
                                                                        )}
                                                                    </td>
                                                                )
                                                            })}
                                                            <td>
                                                                <div className="number-wrapper me-3">
                                                                    {numberFormatter(
                                                                        (() => {
                                                                            const included = propertyReportData.filter((propertyReport) => {
                                                                                const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
                                                                                return created <= endDate
                                                                            })
                                                                            const vals = included.map((pr) => pr.totals ? pr.totals[tableMode] : 0)
                                                                            return vals.length ? calculateMedian(vals) : 0
                                                                        })(),
                                                                        tableMode,
                                                                    )}
                                                                </div>
                                                                {tableMode === 'trueOccupancy' ? (
                                                                    <ProgressBar now={(() => {
                                                                        const included = propertyReportData.filter((propertyReport) => {
                                                                            const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
                                                                            return created <= endDate
                                                                        })
                                                                        const vals = included.map((pr) => pr.totals ? pr.totals.trueOccupancy : 0)
                                                                        return vals.length ? calculateMedian(vals) : 0
                                                                    })()} />
                                                                ) : (
                                                                    <ProgressBar now={(() => {
                                                                        const included = propertyReportData.filter((propertyReport) => {
                                                                            const created = myProperties && myProperties[propertyReport.propertyId] ? new Date(myProperties[propertyReport.propertyId].created).getTime() : 0
                                                                            return created <= endDate
                                                                        })
                                                                        const vals = included.map((pr) => pr.totals ? pr.totals.occupancy : 0)
                                                                        return vals.length ? calculateMedian(vals) : 0
                                                                    })()} />
                                                                )}
                                                            </td>
                                                        </tr>
                                                        )}
                                                        {propertyReportData.map((propertyReport: any) => (
                                                            <PropertyReportRow
                                                                key={propertyReport.propertyId}
                                                                propertyReport={propertyReport}
                                                                dateRangeArray={dateRangeArray}
                                                                dateRangeHash={dateRangeHash}
                                                                myProperties={myProperties}
                                                                tableMode={tableMode}
                                                                percentageMode={percentageMode}
                                                                isSelected={propertiesToRemove.has(propertyReport.propertyId)}
                                                                onToggleRemoval={togglePropertyRemoval}
                                                            />
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </ReportTableSelectionBoundary>
                        )}
                    <hr />
                </Col>
            </Row>

            {fetching && (
                <Row>
                    <Col>
                        <Alert variant="info" className="mb-3">
                            Updating report... current results are being retained until recomputation completes.
                        </Alert>
                    </Col>
                </Row>
            )}

            {myPropertiesLoading === true && (
                <Row>
                    <Col>
                        Loading Properties <Spinner size="sm" animation="border" />
                    </Col>
                </Row>
            )}

            {!myPropertiesLoading && (
                <PropertyMultiSelect
                    selectedProperties={selectedProperties}
                    setSelectedProperties={setSelectedPropertiesMemo}
                    propertyData={myProperties}
                />
            )}

        </Container>
    )
}